import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Adds the `.visible` class to elements matching the given selectors when they
 * scroll into the viewport, mirroring the original site's IntersectionObserver
 * behaviour for `.section-reveal`, `.anim-card`, and `.stack-card`.
 *
 * Re-runs on every page navigation so content rendered by client-side routing is
 * picked up correctly.
 */
export function useScrollReveal() {
  const { pathname } = useLocation();

  useEffect(() => {
    const cleanups: Array<() => void> = [];

    const setup = () => {
      const reveal = (selector: string, threshold: number) => {
        const els = document.querySelectorAll(selector);
        if (!els.length) return;
        const obs = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => {
              if (e.isIntersecting) {
                e.target.classList.add("visible");
                obs.unobserve(e.target);
              }
            });
          },
          { threshold }
        );
        els.forEach((el) => obs.observe(el));
        cleanups.push(() => obs.disconnect());
      };

      reveal(".section-reveal", 0.12);
      reveal(".anim-card", 0.15);
      reveal(".stack-card", 0.15);
    };

    const timer = requestAnimationFrame(() => setup());
    return () => {
      cancelAnimationFrame(timer);
      cleanups.forEach((fn) => fn());
    };
  }, [pathname]);
}
